import { HttpClient } from '@angular/common/http';
import { Component, ElementRef, OnDestroy, OnInit, ViewChild, afterNextRender, inject } from '@angular/core';
import { MotionService } from '../../../../../motion/motion.service';

interface GeocodeResponse {
  status: string;
  error_message?: string;
  results: { geometry: { location: google.maps.LatLngLiteral } }[];
}

/**
 * Home #contact: a calm arrival. Headings and details reveal through the shared directives;
 * the Bitrix form and the map only fade (opacity), never move.
 */
@Component({
  selector: 'app-home-contact',
  templateUrl: './home-contact.component.html',
  styleUrl: './home-contact.component.css',
})
export class HomeContactSectionComponent implements OnInit, OnDestroy {
  private readonly http = inject(HttpClient);
  private readonly motion = inject(MotionService);

  @ViewChild('leadForm', { static: true }) private leadForm?: ElementRef<HTMLElement>;
  private formObserver?: IntersectionObserver;

  /**
   * The Maps script is loaded by index.html. When it is blocked or fails, <google-map> throws on
   * construction and takes the whole home page down with it, so the map is only created when the
   * API is there (checked once); otherwise a link card to Google Maps is shown.
   */
  readonly mapsReady = typeof google !== 'undefined' && !!google.maps;

  center: google.maps.LatLngLiteral = { lat: 40.182989, lng: 44.520092 };
  zoom = 17;
  markerOptions: google.maps.MapOptions = { draggable: false };
  address: string = '19/1 Sayat-Nova Ave, Yerevan, Armenia';
  latLng: { lat: number; lng: number } | null = null;
  private geocodingApiUrl = 'https://maps.googleapis.com/maps/api/geocode/json';
  private apiKey = 'AIzaSyCmqs9daS86SEoT1-H3Pw7NLYBBPYgPt0w';

  constructor() {
    afterNextRender(() => this.motion.outside(() => this.watchFormUnderScrollTop()));
  }

  ngOnInit(): void {
    if (this.mapsReady) this.getLatLngFromAddress();
  }

  ngOnDestroy(): void {
    this.formObserver?.disconnect();
  }

  getLatLngFromAddress(): void {
    const url = `${this.geocodingApiUrl}?address=${encodeURIComponent(this.address)}&key=${this.apiKey}`;
    this.http.get<GeocodeResponse>(url).subscribe({
      next: (response) => {
        if (response.status === 'OK' && response.results.length > 0) {
          const location = response.results[0].geometry.location;
          this.latLng = { lat: location.lat, lng: location.lng };
          this.center = { lat: location.lat, lng: location.lng };
        } else {
          console.error('Error fetching geocode data:', response.status, response.error_message);
        }
      },
      error: (error) => {
        console.error('HTTP error fetching geocode data:', error);
      },
    });
  }

  /**
   * On phones the floating scroll-to-top button (bottom right) sits on top of the full-width
   * Bitrix submit. Flag the form while it passes through the bottom strip of the viewport, where
   * the button lives, so home-contact.css can hide the button only for that stretch.
   */
  private watchFormUnderScrollTop(): void {
    const form = this.leadForm?.nativeElement;
    if (!form || typeof IntersectionObserver === 'undefined') return;
    this.formObserver = new IntersectionObserver(
      ([entry]) => form.classList.toggle('is-under-scrolltop', entry.isIntersecting),
      { rootMargin: '-90% 0px 0px 0px' }
    );
    this.formObserver.observe(form);
  }
}
